"use client";

import {
  createOneAdminInteraction,
  getOrgById,
  initOrgConfig,
  submitLogin,
} from "@/lib/actions/actions";
import {
  type LoginFormData,
  loginFormSchema,
} from "@/lib/schemas/adminAccountsSchema";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import {
  EVENT_CATEGORIES,
  EVENT_CONTEXTS,
  addAdminToMixpanel,
  identifyUser,
  trackEvent,
} from "@frontend/lib/analytics";
import { AdminAction } from "@frontend/lib/graphql/generated-gql";
import { useStripeReaderContext } from "@frontend/lib/providers/StripeReaderProvider";
import { usePersistentStore } from "@frontend/lib/stores/globalStore";
import { cn } from "@frontend/shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingCircles } from "../elements/LoadingCircles";

const LoginForm = ({ isWaitingRoom }: { isWaitingRoom?: boolean }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitStatus, setShowSubmitStatus] = useState(false);
  const router = useRouter();
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "success") {
      setShowResetSuccess(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);
  const queryClient = useQueryClient();

  const lastPracticeId = usePersistentStore.use.practiceId();
  const setPracticeId = usePersistentStore.use.setPracticeId();
  const setAllPractices = usePersistentStore.use.setAllPractices();

  const setOrgId = usePersistentStore.use.setOrgId();
  const setAdminId = usePersistentStore.use.setAdminId();

  const { resetStripeReaderContext } = useStripeReaderContext();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    resetField,
  } = useForm<LoginFormData>({
    shouldUseNativeValidation: true,
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
      org: "",
    },
  });
  /**
   * Calls auth service login endpoint, and if login successful set the userId in the store and router push to home page.
   * @param loginData login form data (username and password)
   */
  const onSubmit = async (loginData: LoginFormData) => {
    try {
      setSubmitting(true);
      const result = await submitLogin(loginData);
      if (!result.ok) {
        showSnackbar();
        setSubmitting(false);
        resetField("password");
        return;
      }
      const { adminId, orgId } = result;

      const allPractices = (await getOrgById(orgId))?.practices;
      if (allPractices) {
        setAllPractices(allPractices);
      }

      const orgConfig = await initOrgConfig();
      setAdminId(adminId);
      setOrgId(orgId);
      setPracticeId(orgConfig.practice?.practiceId ?? "ALL");

      resetStripeReaderContext();
      createOneAdminInteraction(AdminAction.Login, adminId, {});
      identifyUser(adminId);
      datadogRum.setUser({
        id: adminId,
        email: loginData.email,
        orgId: orgId,
        practiceID: orgConfig.practice?.practiceId?.toString() ?? "ALL",
      });
      datadogLogs.setUser({
        id: adminId,
        email: loginData.email,
        orgId: orgId,
        practiceID: orgConfig.practice?.practiceId?.toString() ?? "ALL",
      });
      addAdminToMixpanel(
        { email: loginData.email, orgId, id: adminId },
        orgConfig.practice?.practiceId?.toString() ?? "ALL",
      );
      trackEvent({
        category: EVENT_CATEGORIES.ADMIN,
        context: EVENT_CONTEXTS.AUTH,
        action: "login",
      });

      // Refetch admin query after successful login to update Pylon chat
      queryClient.refetchQueries({ queryKey: ["getCurrentAdmin"] });

      if (isWaitingRoom) {
        router.push("/roster?showWelcome=true");
      } else {
        router.push("/skylight");
      }
    } catch (error) {
      showSnackbar();
      setSubmitting(false);
      resetField("password");
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const showSnackbar = () => {
    setShowSubmitStatus(true);
  };

  return (
    <div className="flex flex-col bg-white border border-gray-200 rounded-[28px] p-4 w-full">
      {submitting && (
        <div className="mb-[45%] mt-[45%]">
          <LoadingCircles />
        </div>
      )}
      {!submitting && (
        <>
          {!isWaitingRoom && (
            <Image
              width={150}
              height={125}
              alt="logo image"
              src="images/logo.svg"
              className="self-center mt-4"
            />
          )}
          {showSubmitStatus && (
            <p className="text-lg text-[red] text-center">
              Incorrect email or password. Please try again.
            </p>
          )}
          {showResetSuccess && !showSubmitStatus && (
            <p className="text-lg text-[#1f7a3a] text-center">
              Password reset successfully. Please log in with your new password.
            </p>
          )}
          <form
            className="flex flex-col h-full mt-10 min-w-full p-[5%]"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-[30px]">
              <div className="flex w-full gap-4 items-center justify-center">
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="relative w-full">
                      <label
                        data-pdflabel="text-label"
                        htmlFor="email-input"
                        className="text-[#6b7280] absolute left-0 text-sm w-full break-words leading-tight transition-all duration-200 transform -translate-y-[calc(100%+0.5rem)]"
                      >
                        {isWaitingRoom ? "Username" : "Email"}
                      </label>
                      <input
                        data-pdflabel="text-input"
                        id="email-input"
                        type="text"
                        onChange={field.onChange}
                        data-cy="email-input"
                        className={cn(
                          "h-full bg-white w-full border p-2.5 text-sm font-normal transition-all duration-200 rounded-full",
                          error
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-black",
                        )}
                      />
                    </div>
                  )}
                />
              </div>
              {errors.email && (
                <div className="flex w-full gap-4 items-center">
                  <p className="text-[red]">{errors.email.message}</p>
                </div>
              )}
              <div className="flex w-full gap-4 items-center justify-center">
                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="relative w-full">
                      <label
                        data-pdflabel="text-label"
                        htmlFor="password-input"
                        className="text-[#6b7280] absolute left-0 text-sm w-full break-words leading-tight transition-all duration-200 transform -translate-y-[calc(100%+0.5rem)]"
                      >
                        Password
                      </label>
                      <input
                        data-pdflabel="text-input"
                        id="password-input"
                        type={showPassword ? "text" : "password"}
                        onChange={field.onChange}
                        data-cy="password-input"
                        className={cn(
                          "h-full bg-white w-full border rounded-full p-2.5 text-sm font-normal transition-all duration-200 focus:outline-none",
                          error
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-black",
                        )}
                      />
                    </div>
                  )}
                />
              </div>
              {errors.password && (
                <div className="flex w-full gap-4 items-center">
                  <p className="text-[red]">{errors.password.message}</p>
                </div>
              )}

              <button
                type="button"
                className="flex gap-2 h-[42px] sm:ml-0 lg:ml-1.5 items-center hover:underline"
                onClick={toggleShowPassword}
              >
                {showPassword ? <Eye /> : <EyeOff />}
                Show Password
              </button>

              <button
                className="font-medium bg-superscriptBlue w-full p-2.5 h-[52px] rounded-full text-white border-none text-[21px] shadow-[4px_4px_4px_0px_rgba(0,0,0,0.05)] hover:bg-superscriptBlue/80"
                type="submit"
                disabled={submitting}
              >
                Login
              </button>
              <Link
                href="/login/forgot"
                className="w-fit self-center no-underline text-[#004E66] hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default LoginForm;
